import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Phone, Menu, X, ChevronDown, ArrowRight, WhatsApp } from '@/components/ui/Icons';
import { Logo } from '@/components/ui/Logo';
import { Socials } from '@/components/ui/Socials';
import { services } from '@/data/services';
import { company } from '@/data/company';
import { cn } from '@/lib/cn';

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.documentElement.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-40 transition-[background,box-shadow,backdrop-filter] duration-300',
        scrolled || drawerOpen
          ? 'bg-navy-900/95 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.6)] backdrop-blur-sm'
          : 'bg-navy-900/70 backdrop-blur-sm',
      )}
    >
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-x-0 -bottom-px h-px transition-opacity duration-300',
          scrolled ? 'opacity-100' : 'opacity-0',
          'gold-divider',
        )}
      />
      <div className="container-page flex h-16 items-center gap-3 lg:h-20 lg:gap-6">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-3 text-paper-0 transition hover:opacity-90"
          aria-label="Bliss Building Contractors home"
        >
          <Logo decorative interactive className="h-12 lg:h-14" />
          <span className="sr-only">{company.shortName}</span>
        </Link>

        <nav className="ml-auto hidden items-center gap-0.5 lg:flex" aria-label="Main">
          {services.map((s) =>
            s.subservices.length === 0 ? (
              <NavLink key={s.slug} to={`/${s.slug}`} className={navLinkClass}>
                {s.navLabel}
              </NavLink>
            ) : (
              <div
                key={s.slug}
                className="relative"
                onMouseEnter={() => setOpenMenu(s.slug)}
                onMouseLeave={() => setOpenMenu(null)}
                onFocus={() => setOpenMenu(s.slug)}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpenMenu(null);
                }}
              >
                <NavLink
                  to={`/${s.slug}`}
                  className={({ isActive }) => cn(navLinkBase, isActive && 'text-gold-300')}
                >
                  {s.navLabel}
                  <ChevronDown
                    className={cn('h-3 w-3 transition-transform duration-200', openMenu === s.slug && 'rotate-180')}
                  />
                </NavLink>
                <AnimatePresence>
                  {openMenu === s.slug && (
                    <motion.div
                      key="menu"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 top-full pt-2"
                    >
                      <ul className="min-w-[280px] overflow-hidden rounded-lg border border-navy-700 bg-navy-800 p-2 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.6)]">
                        <li>
                          <Link
                            to={`/${s.slug}`}
                            className="flex items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-gold-300 hover:bg-navy-700"
                          >
                            All {s.navLabel}
                            <ArrowRight className="h-3 w-3" />
                          </Link>
                        </li>
                        <li className="my-1 h-px bg-navy-700" />
                        {s.subservices.map((sub) => (
                          <li key={sub.slug}>
                            <Link
                              to={`/${s.slug}/${sub.slug}`}
                              className="block rounded-md px-3 py-2 text-sm text-paper-0/85 hover:bg-navy-700 hover:text-paper-0"
                            >
                              {sub.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ),
          )}
          <NavLink to="/contact" className={navLinkClass}>Contact</NavLink>
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2 lg:ml-0">
          <a
            href={`tel:${company.phoneTel}`}
            className="hidden items-center gap-2 rounded-md border border-gold-400/70 px-3 py-2 text-sm font-semibold text-gold-300 transition hover:bg-gold-400 hover:text-navy-900 xl:inline-flex"
          >
            <Phone className="h-3.5 w-3.5" />
            {company.phone}
          </a>
          <a
            href={`tel:${company.phoneTel}`}
            className="hidden h-10 w-10 items-center justify-center rounded-md border border-gold-400/70 text-gold-300 transition hover:bg-gold-400 hover:text-navy-900 md:inline-flex xl:hidden"
            aria-label={`Call ${company.phone}`}
          >
            <Phone className="h-4 w-4" />
          </a>
          <Link to="/contact" className="hidden btn-gold px-4 py-2.5 text-sm md:inline-flex">
            Get a quote
          </Link>
          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-md text-paper-0 transition hover:bg-navy-800 lg:hidden"
            onClick={() => setDrawerOpen((v) => !v)}
            aria-label={drawerOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={drawerOpen}
          >
            <AnimatePresence mode="wait" initial={false}>
              {drawerOpen ? (
                <motion.span
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <X className="h-5 w-5" />
                </motion.span>
              ) : (
                <motion.span
                  key="open"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Menu className="h-5 w-5" />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {drawerOpen && (
          <motion.div
            key="drawer"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="border-t border-navy-700 bg-navy-900 lg:hidden"
          >
            <div className="container-page max-h-[calc(100dvh-4rem)] overflow-y-auto overflow-x-hidden pb-10 pt-5">
              {/* Quick actions */}
              <div className="grid grid-cols-3 gap-2.5">
                <Link to="/contact" className="btn-gold col-span-3 w-full text-sm">
                  Get a quote
                </Link>
                <a
                  href={`tel:${company.phoneTel}`}
                  className="flex items-center justify-center gap-2 rounded-md border border-gold-400/70 bg-navy-800/40 px-3 py-3 text-sm font-semibold text-gold-300"
                >
                  <Phone className="h-4 w-4" />
                  Call
                </a>
                <a
                  href={`https://wa.me/${company.whatsappTel}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-md border border-[#25D366]/60 bg-[#25D366]/15 px-3 py-3 text-sm font-semibold text-[#25D366]"
                >
                  <WhatsApp className="h-4 w-4" />
                  WhatsApp
                </a>
                <a
                  href={`tel:${company.mobileTel}`}
                  className="flex items-center justify-center gap-2 rounded-md border border-paper-0/15 bg-navy-800/40 px-3 py-3 text-sm font-semibold text-paper-0/85"
                >
                  <Phone className="h-4 w-4" />
                  Mobile
                </a>
              </div>

              <ul className="mt-6 divide-y divide-navy-700 border-y border-navy-700 text-paper-0">
                <li><Link to="/" className="block py-3.5 font-semibold">Home</Link></li>
                {services.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/${s.slug}`} className="block py-3.5 font-semibold text-gold-300">
                      {s.navLabel}
                    </Link>
                    {s.subservices.length > 0 && (
                      <ul className="ml-1 mb-3 space-y-0.5 text-sm text-paper-0/85">
                        {s.subservices.map((sub) => (
                          <li key={sub.slug}>
                            <Link
                              to={`/${s.slug}/${sub.slug}`}
                              className="block rounded-sm px-2 py-1.5 hover:bg-navy-800"
                            >
                              {sub.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
                <li><Link to="/contact" className="block py-3.5 font-semibold">Contact</Link></li>
              </ul>

              <div className="mt-6">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-gold-300">Follow us</p>
                <Socials variant="navy" size="sm" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

const navLinkBase =
  'inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-paper-0/85 transition hover:text-gold-300';
const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(navLinkBase, isActive && 'text-gold-300');
